import * as React from "react";
import { useCallback, useRef, useState } from "react";
import { Fade, styled } from "@mui/material";
import { GeneralNumericQuestionView } from "./generalNumericQuestion";
import { useNumericInputController } from "./useNumericInputController";
import { QuestionsIF } from "../../QuestionsIF";
import { ErrorText, UnitConverter } from "./numericInput";
import { NumericInputView } from "./ui(Views)/numericInputView";
import { MeasurementUnits } from "fe-common/graphql";
import { useUnitConverter } from "./logic(controllerHooks)/useUnitConveter";
import { useRangeRefinements } from "../rangeQuestionComponents/hooks/useRangeRefinements";
import {
  MeasurementUnitsToString,
  qualifierValueToQualifierFragment,
} from "../../../../utils/dataHelpers";
import { UnitChangerView } from "./ui(Views)/unitChangerView";
import clsx from "clsx";
import { useTranslate } from "../../../../hooks/useTranslate";

//region [[ Styles ]]

const HeightQuestionView = styled(GeneralNumericQuestionView)({});

const HeightContainer = styled("div")({
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  width: "100%",
});

const InputContainers = styled("div")({
  display: "flex",
  flexFlow: "row",
  width: "100%",
  justifyContent: "space-between",
});

const MetricOrFeetNumericInput = styled(NumericInputView)({
  transition: "all 0.3s ease-out",
  flex: 10,
  height: "40px",
  margin: "0 3px",
  "&.imperial": {
    flex: 1,
  },
});

const InchesNumericInput = styled(MetricOrFeetNumericInput)({
  flex: 1,
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface HeightQuestionProps extends QuestionsIF {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]
const INCH_IN_CM = 2.54; // Every inch has 2.54 cm

const FEET_IN_INCH = 12; // Every foot has 12 inches

const feetFromInches = (inches) => Math.floor(inches / FEET_IN_INCH);

const inchesFromBigInches = (inches) =>
  parseFloat((inches % FEET_IN_INCH).toFixed(0));

const inchesFromFeet = (feet) => feet * FEET_IN_INCH;

const heightUnitConverters: UnitConverter[] = [
  {
    unit: MeasurementUnits.Inches,
    displayText: "ft",
    converters: [
      {
        from: MeasurementUnits.Centimeters,
        converter: (cm) => cm / INCH_IN_CM,
      },
    ],
  },
  {
    unit: MeasurementUnits.Centimeters,
    converters: [
      { from: MeasurementUnits.Inches, converter: (inch) => inch * INCH_IN_CM },
    ],
  },
];

export const HeightQuestion = ({
  answer,
  onAnswer,
  question,
  ...props
}: HeightQuestionProps) => {
  const { translate } = useTranslate();

  const suggestion = question?.suggestions?.[0];

  const [answerValue, setAnswerValue] = useState(null);

  const { rangeItems, relation, abstractSuggestion } = useRangeRefinements({
    suggestion,
  });

  const { handleUnitChange, currentUnitConverter } = useUnitConverter({
    rangeItems,
    unitConverters: heightUnitConverters,
  });

  const handleSubmit = useCallback(() => {
    if (answerValue) onAnswer([answerValue]);
  }, [onAnswer, answerValue]);

  const {
    value,
    stringValue,
    errorText,
    handleValueChanged,
  } = useNumericInputController({
    suggestion,
    onValidValue: setAnswerValue,
    currentUnitConverter,
  });

  const skip = useCallback(() => {
    const qualifiers = rangeItems.map((rangeItem) =>
      // Make sure to mark it as numeric so we don't lose the range as we assign the relation
      qualifierValueToQualifierFragment(rangeItem.value, relation, true),
    );

    const answerValue = [
      {
        ...abstractSuggestion,
        qualifiers,
        skip: true,
      },
    ];

    onAnswer(answerValue, true);
  }, [rangeItems, relation, abstractSuggestion, onAnswer]);

  const inchesRef = useRef<HTMLInputElement>(null);

  const metricOrFeetValue =
    currentUnitConverter?.unit === MeasurementUnits.Centimeters
      ? stringValue
      : feetFromInches(value || 0);

  const inchesVale =
    currentUnitConverter?.unit === MeasurementUnits.Inches
      ? inchesFromBigInches(value)
      : 0;

  const handleMetricOrFeetChange = (value: string) => {
    if (currentUnitConverter?.unit === MeasurementUnits.Centimeters) {
      handleValueChanged(value);
      return;
    }
    handleValueChanged(
      (inchesFromFeet(parseFloat(value)) + (inchesVale || 0)).toFixed(0),
    );
  };

  const handleInchesChange = (value: string) => {
    handleValueChanged(
      (inchesFromFeet(metricOrFeetValue) + (parseFloat(value) || 0)).toFixed(0),
    );
  };

  const isImperial = currentUnitConverter?.unit === MeasurementUnits.Inches;

  const handleMetricOrFeetEnter = () => {
    if (!isImperial) {
      handleSubmit();
      return;
    }
    // Move to inches box
    if (inchesRef.current) inchesRef.current.focus();
  };

  const metricOrFeetPlaceholder = isImperial
    ? translate(MeasurementUnitsToString(MeasurementUnits.Feet))
    : translate(MeasurementUnitsToString(MeasurementUnits.Centimeters));

  const inchesPlaceholder = translate(
    MeasurementUnitsToString(MeasurementUnits.Inches),
  );

  return (
    <HeightQuestionView
      isAnswered={!!answer}
      onSubmitClick={handleSubmit}
      questionText={suggestion.question.text}
      onSkipClicked={skip}
      submitDisabled={!answerValue}
      preventSubmit={props.miniView}
      preventSkip={props.miniView}
    >
      <HeightContainer>
        <InputContainers>
          <MetricOrFeetNumericInput
            className={clsx("metric-or-feet", isImperial ? "imperial" : "")}
            value={metricOrFeetValue}
            onChange={handleMetricOrFeetChange}
            placeholder={metricOrFeetPlaceholder}
            onEnter={handleMetricOrFeetEnter}
          />
          <Fade in={isImperial} mountOnEnter unmountOnExit timeout={100}>
            <InchesNumericInput
              className={"inches"}
              ref={inchesRef}
              value={inchesVale === 0 ? "0" : inchesVale}
              onChange={handleInchesChange}
              placeholder={inchesPlaceholder}
              onEnter={handleSubmit}
            />
          </Fade>
          <UnitChangerView
            currentUnitConverter={currentUnitConverter}
            onUnitConverterSelected={handleUnitChange}
            unitConverts={heightUnitConverters}
          />
        </InputContainers>
        <Fade in={!!errorText} mountOnEnter unmountOnExit>
          <ErrorText> {errorText}</ErrorText>
        </Fade>
      </HeightContainer>
    </HeightQuestionView>
  );
};
